/**
 * Hook custom : retourne un callback debounced + helper `.cancel()`.
 *
 * Pas de dep externe (lodash etc.).
 *
 * Codex review S2.6 P2 : si l utilisateur revient a la valeur sauvegardee
 * pendant la fenetre debounce, il faut pouvoir annuler le timer pour ne pas
 * envoyer de PATCH inutile/stale. D'ou l ajout de `cancel`.
 *
 * Usage :
 *   const debouncedSave = useDebouncedCallback(save, 500);
 *   debouncedSave(v);
 *   debouncedSave.cancel(); // si on revert
 */

import { useRef, useCallback, useEffect } from 'react';

/**
 * @template {(...args: any[]) => any} F
 * @param {F} callback
 * @param {number} delayMs
 * @returns {((...args: Parameters<F>) => void) & { cancel: () => void }}
 */
export function useDebouncedCallback(callback, delayMs) {
  const timer = useRef(null);
  const cbRef = useRef(callback);

  // Toujours utiliser la derniere version du callback (closure stable)
  useEffect(() => { cbRef.current = callback; }, [callback]);

  // Cancel pending sur unmount
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const debounced = useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      cbRef.current(...args);
    }, delayMs);
  }, [delayMs]);

  // Attache cancel comme propriete (preserve la signature callable)
  debounced.cancel = cancel;
  return debounced;
}
